import React, { useEffect, useRef, useState } from 'react';
import { createChart, LineSeries, createSeriesMarkers } from 'lightweight-charts';
import { regimeDisplay } from '../utils/adxChartData';
import {
  biasDisplay,
  buildRsiMarkers,
  levelLineColors,
  rsiChartTime,
} from '../utils/rsiChartData';
import './RsiDashboard.css';

const COLOR_PRICE = '#16244A';
const COLOR_RSI   = '#4A17A1';

const TF_CONFIG = {
  daily: { label: 'D',  days: 30, rsiTf: 'daily' },
  '4h':  { label: '4H', days: 10, rsiTf: '4h'    },
  '1h':  { label: '1H', days: 3,  rsiTf: '1h'    },
};

const TIMEFRAMES = ['daily', '4h', '1h'];

function dedupSort(arr) {
  const m = new Map();
  arr.forEach(p => m.set(p.time, p));
  return Array.from(m.values()).sort((a, b) => a.time - b.time);
}

function RsiDashboard() {
  const priceContainerRef = useRef(null);
  const rsiContainerRef   = useRef(null);

  const priceChartRef = useRef(null);
  const rsiChartRef   = useRef(null);
  const priceLineRef  = useRef(null);
  const rsiLineRef    = useRef(null);
  const level70Ref    = useRef(null);
  const level55Ref    = useRef(null);
  const level50Ref    = useRef(null);
  const level45Ref    = useRef(null);
  const level30Ref    = useRef(null);
  const seriesMarkersRef = useRef(null);
  const rsiRowsRef    = useRef([]);

  const [pair, setPair] = useState('');
  const [availablePairs, setAvailablePairs] = useState([]);
  const [timeframe, setTimeframe] = useState('daily');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latest, setLatest] = useState(null);

  useEffect(() => {
    if (!priceContainerRef.current || !rsiContainerRef.current) return;

    const sharedTsOpts = {
      borderColor: '#E3D9C6',
      timeVisible: true,
      secondsVisible: false,
    };

    const priceChart = createChart(priceContainerRef.current, {
      layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid: { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: '#E3D9C6' },
      timeScale: sharedTsOpts,
      width: priceContainerRef.current.clientWidth,
      height: priceContainerRef.current.clientHeight,
    });

    const priceLine = priceChart.addSeries(LineSeries, {
      color: COLOR_PRICE, lineWidth: 2,
      priceLineVisible: false, lastValueVisible: true, title: 'Close',
    });

    const rsiChart = createChart(rsiContainerRef.current, {
      layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid: { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: '#E3D9C6', scaleMargins: { top: 0.05, bottom: 0.05 } },
      timeScale: { ...sharedTsOpts, visible: false },
      width: rsiContainerRef.current.clientWidth,
      height: rsiContainerRef.current.clientHeight,
    });

    const rsiLine = rsiChart.addSeries(LineSeries, {
      color: COLOR_RSI, lineWidth: 2,
      priceLineVisible: false, lastValueVisible: true, title: 'RSI',
    });
    const line70 = rsiChart.addSeries(LineSeries, {
      color: 'rgba(192, 87, 31, 0.20)', lineWidth: 1, lineStyle: 2,
      priceLineVisible: false, lastValueVisible: false, title: '70',
    });
    const line55 = rsiChart.addSeries(LineSeries, {
      color: 'rgba(74, 86, 114, 0.27)', lineWidth: 1, lineStyle: 2,
      priceLineVisible: false, lastValueVisible: false, title: '55',
    });
    const line50 = rsiChart.addSeries(LineSeries, {
      color: '#4A5672', lineWidth: 1, lineStyle: 2,
      priceLineVisible: false, lastValueVisible: false, title: '50',
    });
    const line45 = rsiChart.addSeries(LineSeries, {
      color: 'rgba(74, 86, 114, 0.27)', lineWidth: 1, lineStyle: 2,
      priceLineVisible: false, lastValueVisible: false, title: '45',
    });
    const line30 = rsiChart.addSeries(LineSeries, {
      color: 'rgba(31, 78, 140, 0.20)', lineWidth: 1, lineStyle: 2,
      priceLineVisible: false, lastValueVisible: false, title: '30',
    });

    priceChartRef.current = priceChart;
    rsiChartRef.current = rsiChart;
    priceLineRef.current = priceLine;
    seriesMarkersRef.current = createSeriesMarkers(priceLine, []);
    rsiLineRef.current = rsiLine;
    level70Ref.current = line70;
    level55Ref.current = line55;
    level50Ref.current = line50;
    level45Ref.current = line45;
    level30Ref.current = line30;

    let syncP = false;
    let syncR = false;
    priceChart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (syncR) return;
      syncP = true;
      rsiChart.timeScale().setVisibleLogicalRange(range);
      syncP = false;
    });
    rsiChart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (syncP) return;
      syncR = true;
      priceChart.timeScale().setVisibleLogicalRange(range);
      syncR = false;
    });

    const roP = new ResizeObserver((entries) => {
      for (const e of entries) {
        priceChart.applyOptions({ width: e.contentRect.width, height: e.contentRect.height });
      }
    });
    roP.observe(priceContainerRef.current);

    const roR = new ResizeObserver((entries) => {
      for (const e of entries) {
        rsiChart.applyOptions({ width: e.contentRect.width, height: e.contentRect.height });
      }
    });
    roR.observe(rsiContainerRef.current);

    return () => {
      roP.disconnect();
      roR.disconnect();
      priceChart.remove();
      rsiChart.remove();
      priceChartRef.current = rsiChartRef.current = null;
      priceLineRef.current = rsiLineRef.current = null;
      seriesMarkersRef.current = null;
      level70Ref.current = level55Ref.current = level50Ref.current = null;
      level45Ref.current = level30Ref.current = null;
    };
  }, []);

  useEffect(() => {
    fetch('/api/config/pairs')
      .then(r => r.json())
      .then(data => {
        setAvailablePairs(data.pairs || []);
        setPair(data.default || (data.pairs && data.pairs[0]) || '');
      })
      .catch(() => setError('Failed to load trading pairs.'));
  }, []);

  const applyReferenceLines = (sortedRsi, isRanging) => {
    if (sortedRsi.length === 0) return;
    const refTimes = sortedRsi.map(p => p.time);
    const colors = levelLineColors(isRanging);

    level70Ref.current?.applyOptions({ color: colors.l70 });
    level30Ref.current?.applyOptions({ color: colors.l30 });
    level45Ref.current?.applyOptions({ color: colors.l45 });
    level55Ref.current?.applyOptions({ color: colors.l55 });

    level70Ref.current?.setData(refTimes.map(t => ({ time: t, value: 70 })));
    level55Ref.current?.setData(refTimes.map(t => ({ time: t, value: 55 })));
    level50Ref.current?.setData(refTimes.map(t => ({ time: t, value: 50 })));
    level45Ref.current?.setData(refTimes.map(t => ({ time: t, value: 45 })));
    level30Ref.current?.setData(refTimes.map(t => ({ time: t, value: 30 })));
  };

  useEffect(() => {
    if (!pair || !rsiLineRef.current) return;

    const cfg = TF_CONFIG[timeframe];

    setLoading(true);
    setError(null);

    fetch(
      `/api/indicators/rsi?pair=${encodeURIComponent(pair)}&timeframe=${cfg.rsiTf}&days=${cfg.days}&warmup=false`,
    )
      .then((r) => {
        if (!r.ok) {
          return r.json().then((body) => {
            throw new Error(body.error || `HTTP ${r.status}`);
          });
        }
        return r.json();
      })
      .then((json) => {
        const rows = json.data || [];
        rsiRowsRef.current = rows;

        const priceData = [];
        const rsiData = [];

        rows.forEach((row) => {
          const t = rsiChartTime(row.time, timeframe);
          if (row.close != null) priceData.push({ time: t, value: parseFloat(row.close) });
          if (row.rsi != null) rsiData.push({ time: t, value: parseFloat(row.rsi) });
        });

        const sortedPrice = dedupSort(priceData);
        const sortedRsi = dedupSort(rsiData);

        const snap = json.latest || (rows.length > 0 ? rows[rows.length - 1] : null);
        setLatest(snap);

        if (priceLineRef.current) {
          priceLineRef.current.setData(sortedPrice);
          priceChartRef.current.timeScale().fitContent();
        }

        if (rsiLineRef.current) {
          rsiLineRef.current.setData(sortedRsi);
          applyReferenceLines(sortedRsi, Boolean(snap?.is_ranging));
          rsiChartRef.current.timeScale().fitContent();
        }

        seriesMarkersRef.current?.setMarkers(
          buildRsiMarkers(rows, timeframe),
        );

        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || 'Failed to load RSI data.');
        setLoading(false);
      });
  }, [pair, timeframe]);

  const biasInfo = biasDisplay(latest?.rsi_bias, latest?.rsi_extreme);
  const adxRegime = regimeDisplay(latest?.adx_regime);
  const cfg = TF_CONFIG[timeframe];
  const rangingHint = latest?.is_ranging ? '30/70 active' : '30/70 dim (trend)';

  return (
    <div className="rsi-dashboard">
      <div className="rsi-controls">
        <label htmlFor="rsi-pair-select" className="pair-label">Pair</label>
        <select
          id="rsi-pair-select"
          value={pair}
          onChange={e => setPair(e.target.value)}
          className="pair-select"
          disabled={availablePairs.length === 0}
        >
          {availablePairs.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        <div className="rsi-tf-group">
          {TIMEFRAMES.map(tf => (
            <button
              key={tf}
              className={`rsi-tf-btn${timeframe === tf ? ' active' : ''}`}
              onClick={() => setTimeframe(tf)}
            >
              {TF_CONFIG[tf].label}
            </button>
          ))}
        </div>

        <span className="rsi-range-badge">
          {cfg.days === 30 ? '1M' : `${cfg.days}D`}
        </span>

        <div className="rsi-divider" />

        <div className="rsi-legend">
          <span className="rsi-legend-item rsi-color">
            RSI&nbsp;<strong>{latest?.rsi != null ? latest.rsi.toFixed(1) : '—'}</strong>
          </span>
          <span className={`rsi-regime-badge ${biasInfo.cls}`}>{biasInfo.label}</span>
          {latest?.adx_regime && (
            <span className={`rsi-adx-regime-badge ${adxRegime.cls}`}>{adxRegime.label}</span>
          )}
          {latest?.adx_direction && (
            <span className={`rsi-direction-badge ${latest.adx_direction === 'bull' ? 'dir-bull' : 'dir-bear'}`}>
              {latest.adx_direction === 'bull' ? '▲ Bull' : '▼ Bear'}
            </span>
          )}
          <span className={`rsi-range-hint ${latest?.is_ranging ? 'active' : ''}`}>{rangingHint}</span>
          {loading && <span className="rsi-loading-dot" />}
        </div>
      </div>

      <div className="rsi-charts-area">
        {loading && (
          <div className="chart-overlay">
            <div className="spinner" />
            <span>Loading…</span>
          </div>
        )}
        {error && !loading && (
          <div className="chart-overlay error">
            <span>⚠️ {error}</span>
          </div>
        )}

        <div className="rsi-price-wrapper">
          <div className="rsi-sub-label">Close Price</div>
          <div ref={priceContainerRef} className="rsi-chart-container" />
        </div>

        <div className="rsi-sub-wrapper">
          <div className="rsi-sub-label">RSI (14) · 45–55 neutral</div>
          <div ref={rsiContainerRef} className="rsi-chart-container" />
        </div>
      </div>
    </div>
  );
}

export default RsiDashboard;
